/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import "../UploadOrDecline.scss";

import { ActionType, EventName } from "@microsoftit/telemetry-extensions-npm";
import {
  ApplicationStatus,
  uploadFileSizeLimitInMB,
  TelemetryHistoryLogs,
  uploadFileFormats,
  DefaultFileUploadName,
} from "../../constants";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IconButton,
  Link,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import React, { useState } from "react";

import { applicationApi } from "../../../services/application-apis/application-apis";
import { logTelemetry } from "../../../services/telemetry/telemetryWrapper";
import {
  uploadDeclineDialogStyle,
  uploadedFileButtonStyle,
  messageBarStyle,
} from "../UploadOrDecline.styles";
import { uploadFileToBlob } from "../azure-storage-blob";
import { triggerReferenceHistory } from "../../reference-history/reference-middle-layer";
import { getURLParams } from "../../util";
import floodgateService from "../../../services/survey/FloodgateService";

interface Props {
  hideUploadDialog: boolean;
  toggleUploadDialog: () => void;
  setStatus: (status: ApplicationStatus) => void;
  status: ApplicationStatus;
}

const uploadDialogContent = {
  type: DialogType.normal,
  title: "Upload reference letter",
  closeButtonAriaLabel: "Close",
  subText: `Any file size up to ${uploadFileSizeLimitInMB} MB is acceptable. Supported formats are .pdf, .doc, .docx, .txt, .pptx`,
};

const dialogStyles = { main: uploadDeclineDialogStyle };
const modalProps = {
  isBlocking: true,
  styles: dialogStyles,
};

export const UploadDialog: React.FC<Props> = ({
  hideUploadDialog,
  toggleUploadDialog,
  setStatus,
  status,
}) => {
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [inputKey, setInputKey] = useState(Math.random().toString(36));
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isFileSizeExceeds, setIsFileSizeExceeds] = useState(false);
  const [isSupportedFileFormat, setIsSupportedFileFormat] = useState(true);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [isFileAlreadyPresent, setIsFileAlreadyPresent] = useState(false);

  const handleUploadFile = () => {
    if (hiddenFileInput) {
      if (hiddenFileInput.current) hiddenFileInput.current.click();
    }
  };

  const handleFileRemoval = () => {
    //eslint-disable-next-line
    hiddenFileInput.current!.value = "";
    setIsFileSizeExceeds(false);
    setIsSupportedFileFormat(true);
    setFileSelected(null);
    setIsFileAlreadyPresent(false);
    setIsUploadFailed(false);
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files) {
      let fileExtension: any = event.currentTarget.files[0].name;
      fileExtension = fileExtension.split(".");
      fileExtension = fileExtension[fileExtension.length - 1];
      if (!uploadFileFormats.includes(fileExtension.toLowerCase())) {
        setIsSupportedFileFormat(false);
      } else if (
        event.currentTarget.files[0].size / 1048576 >
        uploadFileSizeLimitInMB
      ) {
        setIsFileSizeExceeds(true);
      }
    }
    setFileSelected(event.currentTarget.files && event.currentTarget.files[0]);
  };

  const triggerTelemetry = () => {
    let featureName = "Reference - Upload letter";
    if (status === ApplicationStatus.DECLINED) {
      featureName = "Reference - Upload post-decline";
    } else if (status === ApplicationStatus.APPROVED) {
      featureName = "Reference - Re-upload letter";
    }
    logTelemetry(
      featureName,
      featureName,
      ActionType.User,
      featureName,
      EventName.ButtonClick
    );
  };

  const onFileUpload = async () => {
    try {
      setIsFileUploading(true);
      const data = await applicationApi.getReFerenceMetadata().then();
      const response = data.data.operationResult.result;
      if (response.status !== ApplicationStatus.PENDING) {
        if (fileSelected && fileSelected !== null) {
          setIsFileAlreadyPresent(false);
          setIsUploadFailed(false);
          let fileDate: any = new Date();
          fileDate = `${
            fileDate.getUTCMonth() + 1
          }${fileDate.getUTCDate()}${fileDate.getUTCFullYear()}${fileDate.getUTCHours()}${fileDate.getUTCMinutes()}${fileDate.getUTCSeconds()}`;
          const blobAccessToken = await applicationApi.getBlobAccessToken();
          const newFile = new File(
            [fileSelected],
            `${
              getURLParams().referenceId
            }${DefaultFileUploadName}${fileDate}.${fileSelected.name
              .split(".")
              .pop()}`,
            { type: fileSelected.type }
          );
          await uploadFileToBlob(newFile, blobAccessToken.data);
          triggerTelemetry();
          setInputKey(Math.random().toString(36));
          handleFileRemoval();
          toggleUploadDialog();
          setStatus(ApplicationStatus.PENDING);
          triggerReferenceHistory(TelemetryHistoryLogs.UploadReference);
          await floodgateService.startEngine();
          floodgateService.triggerRequestCompletedActivity();
        }
      } else {
        setIsFileAlreadyPresent(true);
      }
    } catch (e) {
      setIsUploadFailed(true);
      setIsFileAlreadyPresent(false);
    } finally {
      setIsFileUploading(false);
    }
  };

  React.useEffect(() => {
    if (fileSelected !== null) {
      setFileName(fileSelected.name);
    } else {
      setFileName("");
    }
  }, [fileSelected]);

  const onDismissDialog = () => {
    handleFileRemoval();
    setIsFileUploading(false);
    setIsUploadFailed(false);
    setIsFileAlreadyPresent(false);
    toggleUploadDialog();
  };

  return (
    <Dialog
      hidden={hideUploadDialog}
      onDismiss={onDismissDialog}
      dialogContentProps={uploadDialogContent}
      modalProps={modalProps}
      className="ms-ac-referenceview-upload-dialog"
    >
      <div className="ms-ac-referenceview-upload-con">
        {fileSelected ? (
          <DefaultButton
            className="ms-ac-referenceview-uploaded-file-btn"
            iconProps={{ iconName: "SkypeCircleCheck" }}
            text="Uploaded"
            allowDisabledFocus
            disabled={true}
            styles={uploadedFileButtonStyle}
          />
        ) : (
          <DefaultButton
            className="ms-ac-referenceview-upload-file-btn"
            iconProps={{ iconName: "Upload" }}
            text="Upload a file"
            onClick={handleUploadFile}
            allowDisabledFocus
            disabled={false}
          />
        )}
        <p className="ms-ac-referenceview-selected-file">{fileName}</p>
        {fileName ? (
          <IconButton
            onClick={handleFileRemoval}
            iconProps={{ iconName: "Delete" }}
            title="Delete"
            ariaLabel="Delete"
          />
        ) : (
          <></>
        )}
      </div>
      {isFileSizeExceeds ? (
        <div className="ms-ac-referenceview-upload-error">
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            dismissButtonAriaLabel="Close"
          >
            Please upload a file less than {uploadFileSizeLimitInMB} MB.
          </MessageBar>
        </div>
      ) : (
        <></>
      )}
      {!isSupportedFileFormat ? (
        <div className="ms-ac-referenceview-upload-error">
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            dismissButtonAriaLabel="Close"
          >
            Invalid File Format.
          </MessageBar>
        </div>
      ) : (
        <></>
      )}
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={onFileChange}
        style={{ display: "none" }}
        key={inputKey || ""}
        accept=".pdf,.doc,.docx,.txt,.pptx"
      />
      {fileSelected && !isFileSizeExceeds && isSupportedFileFormat ? (
        <DialogFooter>
          {isFileUploading ? (
            <div className="ms-ac-referenceview-submitting-con">
              <Spinner size={SpinnerSize.medium} />
              <PrimaryButton text="Submitting" disabled={true} />
            </div>
          ) : (
            <div className="ms-ac-referenceview-custom-footer">
              {isUploadFailed ? (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline={false}
                  dismissButtonAriaLabel="Close"
                  truncated={false}
                  styles={messageBarStyle}
                >
                  Sorry, we are unable to submit your letter now. Please refresh
                  and try again. If the issue persists,&nbsp;
                  <Link
                    href={process.env.REACT_APP_CAREERS_PORTAL_SUPPORT_URI}
                    target="_blank"
                    underline
                    componentRef={(self) => self?.focus()}
                  >
                    contact support
                  </Link>
                </MessageBar>
              ) : (
                <></>
              )}

              {isFileAlreadyPresent ? (
                <MessageBar
                  messageBarType={MessageBarType.warning}
                  isMultiline={false}
                  dismissButtonAriaLabel="Close"
                  truncated={false}
                  styles={messageBarStyle}
                >
                  Sorry, we are unable to submit the updated letter now as the
                  previous upload is in progress. Please try again after a few
                  minutes.
                </MessageBar>
              ) : (
                <></>
              )}
              <PrimaryButton onClick={onFileUpload} text="Submit" />
            </div>
          )}
        </DialogFooter>
      ) : (
        <></>
      )}
    </Dialog>
  );
};
